import { useMediaQuery } from '@mui/material';
import { isToday } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { AREA_WIDGET_EVENT, StorePerformanceInterval, WIDGET_CATEGORY_EVENT } from '../../constants';
import { LocalStorageKey } from '../../constants/localStorageKey';
import { useThemeContext } from '../../global-state/themeContext';
import { useTransactions, useUser } from '../../hooks';
import useEventTracker from '../../hooks/useEventTracker';
import { usePageEventTracker } from '../../hooks/usePageEventTracker';
import { Container, Icons, Tooltip, Typography } from '../../stories/atoms';
import { ColumnFlexContainer } from '../../stories/atoms/containerV2/ContainerV2';
import { ActionButton, Banner, BannerItemsType, DropdownMenu, IconWithText } from '../../stories/molecules';
import { Switch } from '../../stories/molecules/switch/Switch';
import { Page } from '../../stories/organisms';
import { muiTheme } from '../../theme';
import { homeTranslations } from '../../translations';
import { getDatePickerLabel, getUniqueId } from '../../utils';
import { StyledCategoryContainer, StyledIcon, StyledSwitchWrapper } from './Home.styles';
import { HomeDatePicker } from './HomeDatePicker';
import { WidgetsCategory } from './types';
import { canViewHighlightedWidgets, categoryIcons, getPermissionToViewCategory } from './util';
import { WidgetsContainer } from './widgetsContainer/WidgetsContainer';

export type SelectedCategory = {
  selectedId: string;
  selectedName: WidgetsCategory;
};

export type CategoryIcon = {
  iconId: string;
  iconName: WidgetsCategory;
  icon: Icons;
};

export const Home: FC = () => {
  const { t } = useTranslation();
  const { widgetCategory, datePicker, common } = homeTranslations;
  const {
    get: { stores, currentStoreId, highlightedKpiTargets, userPermissions },
  } = useUser();

  const {
    get: { isFetchingTransaction, isFetchingCount },
    set: setTransactions,
  } = useTransactions();
  const { search, pathname } = useLocation();
  const { mode } = useThemeContext();
  const trackEvent = useEventTracker();

  const newFeatureLocalStorageValue = sessionStorage.getItem(LocalStorageKey.ShowNewFeatureBanner);

  usePageEventTracker('home');

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const canViewAreaComparison = userPermissions.areaComparisonWidget.canRead;

  const canViewSalesPage = userPermissions.salesOverviewPage.canRead;
  const canViewAssetManagementPage = userPermissions.assetManagementWidget.canRead;
  const canViewNewFeatureBanner =
    canViewAssetManagementPage && !canViewSalesPage && newFeatureLocalStorageValue === null;

  const queryParams = new URLSearchParams(search);
  const storeIdParam = queryParams.get('storeId');
  const mobileDeviceKey = queryParams.get('key');
  const showHomeForNonSignInUser =
    storeIdParam && mobileDeviceKey && pathname.includes('/home') ? true : undefined;

  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory>(() => {
    if (mobileDeviceKey) {
      return { selectedId: 'allWidgets', selectedName: WidgetsCategory.ALL };
    } else {
      const savedIconStr = localStorage.getItem('selectedIcon');

      // if no cached category but there are highlighted widgets, default to
      // highlighted widgets
      if (!savedIconStr && highlightedKpiTargets.length > 0) {
        return { selectedId: 'highlightedWidgets', selectedName: WidgetsCategory.HIGHLIGHTED };
      }

      // if no cached category, default to customer experience widgets
      if (!savedIconStr) {
        return { selectedId: 'salesEfficiency', selectedName: WidgetsCategory.SALESEFFICIENCY };
      }

      const savedIcon = JSON.parse(savedIconStr) as SelectedCategory;

      // if cached category is highlighted widgets but there are no highlighted widgets, return customer experience widgets instead
      if (savedIcon.selectedName === WidgetsCategory.HIGHLIGHTED && highlightedKpiTargets.length === 0) {
        return { selectedId: 'salesEfficiency', selectedName: WidgetsCategory.SALESEFFICIENCY };
      }

      return savedIcon;
    }
  });

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentInterval, setCurrentInterval] = useState<StorePerformanceInterval>(StorePerformanceInterval.DAILY);
  const [bannerItems, setBannerItems] = useState<BannerItemsType[]>([]);

  const [dateLabel, setDateLabel] = useState(
    getDatePickerLabel(
      selectedDate,
      currentInterval,
      t(datePicker.weekLabel.key, datePicker.weekLabel.defaultValue),
    ),
  );

  const [showAreaWidget, setShowAreaWidget] = useState<boolean>(
    localStorage.getItem('showAreaComparisonWidget') === 'true',
  );

  useEffect(() => {
    localStorage.setItem('selectedIcon', JSON.stringify(selectedCategory));
  }, [selectedCategory]);

  useEffect(() => {
    if (!isFetchingTransaction) setTransactions({ isFetchingTransaction: true });
    if (!isFetchingCount) setTransactions({ isFetchingCount: true });
  }, [isFetchingTransaction, isFetchingCount]);

  useEffect(() => {
    setBannerItems([
      {
        id: getUniqueId(),
        label: 'Whats New!',
        labelType: 'button',
        content: (
          <ColumnFlexContainer gap={[1]} margin={[1, 0, 0]} padding={[0, 0, 0, 6]}>
            <Typography>
              There are images now available for all products in top sold and top plan products widgets!
            </Typography>
          </ColumnFlexContainer>
        ),
        type: 'info',
        actionButton: ActionButton.Close,
        onClose: () => {
          sessionStorage.setItem(LocalStorageKey.ShowNewFeatureBanner, 'false');
        },
      },
    ]);
  }, []);

  const isCurrentDay = isToday(selectedDate);

  const handleCategorySelect = (iconId: string, iconName: WidgetsCategory) => {
    setSelectedCategory({ selectedId: iconId, selectedName: iconName });
    trackEvent(WIDGET_CATEGORY_EVENT, 'click', [
      { name: 'Page', stringValue: 'home' },
      { name: 'CategorySelected', stringValue: iconName },
    ]);
  };

  const handleDateChange = (date: Date, interval: StorePerformanceInterval) => {
    setSelectedDate(date);
    setCurrentInterval(interval);
    setDateLabel(
      getDatePickerLabel(date, interval, t(datePicker.weekLabel.key, datePicker.weekLabel.defaultValue)),
    );
  };

  const getFilteredCategoryItems = () => {
    const filteredCategories = categoryIcons.filter((categoryIcon) => {
      if (categoryIcon.iconName === WidgetsCategory.HIGHLIGHTED) {
        return (
          highlightedKpiTargets.length > 0 && canViewHighlightedWidgets(highlightedKpiTargets, userPermissions)
        );
      }
      return getPermissionToViewCategory(categoryIcon.iconName, userPermissions);
    });
    return filteredCategories;
  };

  return (
    <Page
      header={
        <StyledCategoryContainer isMobile={isMobile}>
          <div>
            {showHomeForNonSignInUser ? (
              <div data-testid="non-signin-user"></div>
            ) : isMobile ? (
              <DropdownMenu
                data-testid="category-dropdown-menu"
                dropdownLabel={t(
                  widgetCategory[selectedCategory.selectedName].key,
                  widgetCategory[selectedCategory.selectedName].defaultValue,
                )}
                initialSelected={[selectedCategory.selectedName]}
                backgroundColor={{
                  hover: 'baseGray',
                  selected: mode === 'dark' ? 'primary' : 'backgroundSelected',
                  selectedHover: 'secondaryTextGray',
                }}
                menuItems={getFilteredCategoryItems().map((categoryIcon) => ({
                  key: categoryIcon.iconId,
                  item: (
                    <IconWithText
                      label={t(
                        widgetCategory[categoryIcon.iconName].key,
                        widgetCategory[categoryIcon.iconName].defaultValue,
                      )}
                      iconOptions={{
                        icon: categoryIcon.icon,
                        size: 'small',
                      }}
                    />
                  ),
                }))}
                onSelect={(selectedItemKey) => {
                  setSelectedCategory({
                    selectedId: selectedItemKey[0],
                    selectedName: selectedItemKey[0] as WidgetsCategory,
                  });
                  trackEvent(WIDGET_CATEGORY_EVENT, 'click', [
                    { name: 'Page', stringValue: 'home' },
                    { name: 'CategorySelected', stringValue: selectedItemKey[0] },
                  ]);
                }}
              />
            ) : (
              getFilteredCategoryItems().map((categoryIcon) => {
                return (
                  <Tooltip
                    text={
                      <Typography padding={[1]} color="white">
                        {t(
                          widgetCategory[categoryIcon.iconName].key,
                          widgetCategory[categoryIcon.iconName].defaultValue,
                        )}
                      </Typography>
                    }
                    position="bottom"
                    key={`category-icon-${categoryIcon.iconId}`}
                  >
                    <StyledIcon
                      isSelected={selectedCategory.selectedId === categoryIcon.iconId}
                      data-testid="category-icon"
                      icon={categoryIcon.icon}
                      size="small"
                      margin={[0, 4, 0, 0]}
                      onClick={() => handleCategorySelect(categoryIcon.iconId, categoryIcon.iconName)}
                    />
                  </Tooltip>
                );
              })
            )}
          </div>
          <HomeDatePicker
            currentDate={selectedDate}
            dateLabel={dateLabel}
            onDateChange={handleDateChange}
            showMonthyPicker
          />
        </StyledCategoryContainer>
      }
      headerBorderType="dashed"
      body={
        <div>
          {canViewNewFeatureBanner && (
            <ColumnFlexContainer>
              <Banner isSticky items={bannerItems} onChange={(items) => setBannerItems(items)} />
            </ColumnFlexContainer>
          )}
          <Container padding={isMobile ? [4, 5] : [10, 17]} wrap="nowrap" data-testid="home">
            <Container
              direction={isMobile ? 'vertical' : 'horizontal'}
              wrap="nowrap"
              space="between"
              position={undefined}
            >
              <Container wrap="nowrap">
                <Typography type="h3" data-testid="store-name" overflow="visible">
                  {stores.find((store) => store.storeId === currentStoreId)?.popularName +
                    ' ' +
                    `(${currentStoreId})`}
                </Typography>
                <Typography
                  type="body3"
                  data-testid={!isCurrentDay ? 'yesterday-date' : 'selected-date'}
                  overflow="visible"
                >
                  {t(
                    widgetCategory[selectedCategory.selectedName].key,
                    widgetCategory[selectedCategory.selectedName].defaultValue,
                  )}
                  {' - '}
                  {dateLabel}
                </Typography>
              </Container>
              {[WidgetsCategory.ALL, WidgetsCategory.SALESEFFICIENCY].includes(selectedCategory.selectedName) &&
                canViewAreaComparison && (
                  <StyledSwitchWrapper isMobile={isMobile}>
                    <Switch
                      data-testid="area-widget-switch"
                      isActive={showAreaWidget}
                      label={
                        showAreaWidget
                          ? t(common.hideAreaSwitch.key, common.hideAreaSwitch.defaultValue)
                          : t(common.showAreaSwitch.key, common.showAreaSwitch.defaultValue)
                      }
                      labelPosition="left"
                      labelType="body3"
                      style={{ justifyContent: 'flex-end' }}
                      onClick={() => {
                        setShowAreaWidget(!showAreaWidget);
                        localStorage.setItem('showAreaComparisonWidget', JSON.stringify(!showAreaWidget));
                        trackEvent(AREA_WIDGET_EVENT, 'click', [
                          { name: 'Page', stringValue: 'home' },
                          { name: 'WidgetState', stringValue: showAreaWidget ? 'hide' : 'show' },
                        ]);
                      }}
                    />
                  </StyledSwitchWrapper>
                )}
            </Container>
            <WidgetsContainer
              showAreaWidget={showAreaWidget}
              isNonSigninUser={showHomeForNonSignInUser}
              isToday={isCurrentDay}
              selectedCategory={selectedCategory.selectedName}
              selectedDate={selectedDate}
              interval={currentInterval}
            />
          </Container>
        </div>
      }
    />
  );
};
